// askSmart-ui/src/pages/TOSP/budgetHistory.ts
//
// A budget's progression, in the shape SMRT COP uses for sites: each step,
// once completed, records { at, by } and the budget's DISPLAYED status becomes
// that step's status. Status is derived, never stored — so it can't drift out
// of step with the history that produced it.
//
// The steps follow the actual round trip: TOSP projects revenue, the admin
// exports it into QuickBooks, the completed budget comes back for the expense
// side, those expenses are categorised, the management fee is worked out and
// exported in turn, and the whole thing is reviewed.
//
// A status here names the NEXT action, which is what makes it usable as the
// label on the action button — "Export Revenue" is both where the budget is
// and what to do about it.

export interface BudgetHistoryEntry {
  /** ISO timestamp. */
  at: string;
  by?: string;
  byName?: string;
}

export type BudgetHistoryStepKey =
  | "created"
  | "revenueExported"
  | "expensesImported"
  | "categoriesAssigned"
  | "published";

export type BudgetHistory = Partial<
  Record<BudgetHistoryStepKey, BudgetHistoryEntry | null>
>;

export interface BudgetHistoryStep {
  key: BudgetHistoryStepKey;
  /** Shown in the history timeline. */
  label: string;
  /** The budget's status once this is the latest completed step. */
  status: string;
  /** What the step means, under the label. */
  detail: string;
  /**
   * What the action button says when this step is the next one. Defaults to
   * the PREVIOUS step's status, which usually names the action already —
   * set it where the button does something more specific than the status.
   */
  action?: string;
}

// Each step's `status` is the state the budget is IN once that step is the
// latest done — which is the next thing to do. The action button on the budget
// screen is driven off it, so the two can never disagree about what comes next.
export const BUDGET_HISTORY_STEPS: BudgetHistoryStep[] = [
  {
    key: "created",
    label: "Budget Created",
    status: "Export Revenue",
    detail: "Revenue is projected from the leases, tax rolls and units in TOSP.",
  },
  {
    key: "revenueExported",
    label: "Revenue Exported",
    status: "Import CAMS",
    detail: "The projected revenue has been keyed into QuickBooks by hand.",
  },
  // These two are recorded together when the import wizard finishes: it won't
  // let you finish until every expense line has a category, so importing and
  // categorising are one action and neither gets an action button.
  {
    key: "expensesImported",
    label: "Expenses Imported",
    status: "Assign Categories",
    detail: "The completed QBO budget is back and its expense lines are loaded.",
  },
  {
    key: "categoriesAssigned",
    label: "CAM Categories Assigned",
    status: "Review budget",
    detail: "Every expense line carries a category, so the costs can be divided.",
  },
  // THE TWO CALCULATION STEPS ARE GONE, and that is the point.
  //
  // "Export Mgmt Fee" and "Calculate CAMS" each pressed a button that worked a
  // figure out and stored it. A stored figure drifts: edit a category, correct
  // a lease, change a split, and the sheet disagreed with itself until somebody
  // remembered to press them again, in the right order. The fee and the 41001
  // recovery are now derived from the budget's own lines on every render, so
  // they cannot be stale.
  //
  // What those steps were really recording — that the admin keyed the figures
  // into QuickBooks — belongs to the round trip, and Publish records it.
  // REVIEWING IS NOT A STEP OF ITS OWN. It used to be: an admin ticked
  // "Budget Reviewed" to say the figures had been checked, and then pressed
  // Publish to freeze them. Two actions, one decision — and the tick recorded
  // nothing that publishing does not record better, since nobody publishes a
  // budget they have not read.
  //
  // So "Review budget" is a STATUS, not a step: the budget sits there while it
  // is being worked through, and Publish is what finishes it.
  {
    key: "published",
    label: "Budget Published",
    status: "Complete",
    detail:
      "The figures have been checked and posted to QuickBooks. The whole " +
      "financial state is frozen and editing is locked.",
    action: "Publish Budget",
  },
];

const FIRST_STATUS = BUDGET_HISTORY_STEPS[0].status;

/** The status of the latest completed step. */
export function deriveBudgetStatus(history: BudgetHistory | undefined): string {
  let status = FIRST_STATUS;
  if (!history) return status;
  for (const step of BUDGET_HISTORY_STEPS) {
    if (history[step.key]) status = step.status;
  }
  return status;
}

/**
 * The one thing to do next, given where the budget has got to — or null once
 * every step is done and there is nothing left to press.
 *
 * The button is LABELLED with the current status, because a status here is
 * the next action ("Export Revenue", "Import CAMS"), and `step` is what
 * pressing it records. Most steps are the admin declaring they've done
 * something outside TOSP — keyed figures into QuickBooks, checked the numbers
 * — so they complete on click. Importing is the exception: it opens the wizard
 * and only completes when that finishes.
 */
export function nextBudgetAction(history: BudgetHistory | undefined): {
  label: string;
  step: BudgetHistoryStepKey;
  opensImport: boolean;
  publishes: boolean;
} | null {
  if (!history?.created) return null;
  const next = BUDGET_HISTORY_STEPS.find((step) => !history[step.key]);
  if (!next) return null;
  return {
    label: next.action || deriveBudgetStatus(history),
    step: next.key,
    opensImport: next.key === "expensesImported",
    // Publishing is not a tick: it captures the snapshot and locks the budget,
    // so it goes through its own confirmation rather than completing on click.
    publishes: next.key === "published",
  };
}

export interface BudgetTimelineRow {
  key: BudgetHistoryStepKey;
  label: string;
  detail: string;
  status: string;
  done: boolean;
  at?: string;
  byName?: string;
}

export function buildBudgetTimeline(history: BudgetHistory | undefined): BudgetTimelineRow[] {
  return BUDGET_HISTORY_STEPS.map((step) => {
    const entry = history?.[step.key];
    return {
      key: step.key,
      label: step.label,
      detail: step.detail,
      status: step.status,
      done: !!entry,
      at: entry?.at,
      byName: entry?.byName,
    };
  });
}

// Marking a step completes everything up to it — a budget can't be finalised
// without its expenses having arrived. Un-marking clears it and everything
// after, so the history always reads as one unbroken run.
export function setBudgetStep(
  history: BudgetHistory | undefined,
  key: BudgetHistoryStepKey,
  done: boolean,
  actor?: { by?: string; byName?: string },
): BudgetHistory {
  const target = BUDGET_HISTORY_STEPS.findIndex((step) => step.key === key);
  if (target < 0) return history || {};

  const now = new Date().toISOString();
  // Rebuilt from the step list rather than copied and amended, so a step that
  // no longer exists cannot linger. Budgets worked on before the calculation
  // steps were removed still carry `mgmtFeeExported` and `camsCalculated`; they
  // affect nothing, and this clears them on the next write rather than leaving
  // a history that names stages the process no longer has.
  const previous = history || {};
  const next: BudgetHistory = {};

  BUDGET_HISTORY_STEPS.forEach((step, index) => {
    const shouldBeDone = done ? index <= target : index < target;
    // Keep the original timestamp of a step that was already done.
    if (shouldBeDone) next[step.key] = previous[step.key] || { at: now, ...actor };
  });

  return next;
}
